import Footer from '@/components/Footer';
import RightContent from '@/components/RightContent';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { history, RequestConfig, RunTimeLayoutConfig, useQueryClient } from '@umijs/max';
import { App as AntdApp, Spin } from 'antd';
import { useEffect } from 'react';
import defaultSettings from '../config/defaultSettings';
import darkLogo from '../public/logo_black.png';
import whiteLogo from '../public/logo_white.png';
import { errorConfig } from './requestErrorConfig';
import { currentUser as queryCurrentUser } from './services/impayment-api/user';
import { CURRENT_USER_QUERY_KEY, LOGIN_PATH } from './utils/constants';
import { handleTokenExpiration, isTokenValid } from './utils/utils';

const QueryClientSync = ({ currentUser }: { currentUser?: USER_API.User }) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (currentUser) {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, currentUser);
    } else {
      queryClient.removeQueries({ queryKey: CURRENT_USER_QUERY_KEY, exact: true });
    }
  }, [currentUser, queryClient]);

  return null;
};

async function fetchUserInfo(): Promise<USER_API.User | undefined> {
  if (!isTokenValid()) {
    handleTokenExpiration();
    return undefined;
  }

  try {
    const res = await queryCurrentUser({ skipErrorHandler: true });
    return res;
  } catch (error) {
    handleTokenExpiration();
    return undefined;
  }
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: USER_API.User;
  loading?: boolean;
  fetchUserInfo?: () => Promise<USER_API.User | undefined>;
}> {
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings,
  };
}

// ProLayout APIs supported https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    rightContentRender: () => <RightContent />,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser?.email && location.pathname !== LOGIN_PATH) {
        history.push(LOGIN_PATH);
      }

      if (!isTokenValid() && initialState?.currentUser) {
        setInitialState({ ...initialState, currentUser: undefined });
        handleTokenExpiration();
      }
    },
    links: [],
    logo: initialState?.settings?.navTheme === 'realDark' ? whiteLogo : darkLogo,
    menuHeaderRender: undefined,
    // Custom 403 page
    // unAccessible: <div>unAccessible</div>,
    // Add a loading state
    childrenRender: (children) => {
      const content = initialState?.loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>{children}</>
      );

      return (
        <AntdApp>
          <QueryClientSync currentUser={initialState?.currentUser} />
          {content}
        </AntdApp>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request configuration, you can configure error handling
 * It provides a unified network request and
 * error handling scheme based on the useRequest of axios and ahooks.
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  baseURL: API_URL,
};
